/**
 * Key bindings — rebindable from day one (design spec §3.2).
 *
 * Bindings are data, keyed by `KeyboardEvent.code`. `code` rather than `key`
 * because it names the physical key rather than the character it produces: a
 * French keyboard's `KeyA` is where a US keyboard's `KeyQ` is, and a roguelike
 * bound to letters becomes unplayable on a layout it was not designed for.
 * Vi-keys are a *position* on the keyboard as much as they are letters.
 *
 * All three schemes are live at once and out of the box (M0-05): arrows, HJKL
 * with diagonals on YUBN, and the numeric keypad. Nobody should have to
 * configure anything to walk.
 *
 * Two kinds of action, and the difference is in the hand rather than in the
 * type: **`move` is held** — the key repeats a walk for as long as it is down,
 * because auto-repeat rate varies per machine and walking speed must not —
 * while everything else **fires once per press**. `Input` keeps the two apart
 * (`current()` versus `takeCommands()`) so a binding never has to say which it
 * is.
 */

import type { Direction } from '@annwn/protocol';

export type Action =
  | { readonly kind: 'move'; readonly direction: Direction }
  /** Open the chat line. Speech is spatial, so this is a world action. */
  | { readonly kind: 'say' }
  /** Toggle persistent Look mode (M0-06). Tiles are examined by clicking them. */
  | { readonly kind: 'examine' }
  /** Back out of whatever is open. */
  | { readonly kind: 'cancel' }
  /** Open the aim menu — M2-03's called shot, two keystrokes (M2-06). */
  | { readonly kind: 'aim' }
  /** Select or confirm the actor under the look cursor as a target. */
  | { readonly kind: 'target' }
  /** Raise or drop the guard (M2-02). Held stance, toggled by a press. */
  | { readonly kind: 'guard' }
  /** Show the current or last fight in full (M2-06's battle view). */
  | { readonly kind: 'battle' }
  /** Open the verb menu against a helpless body beside you (M2-07). */
  | { readonly kind: 'interact' }
  /** Gather what is under your feet (M3-03). */
  | { readonly kind: 'forage' }
  /** Show what you are carrying (M3-03). */
  | { readonly kind: 'satchel' }
  /** Strike a light from whatever will burn in your satchel (M3-05). */
  | { readonly kind: 'kindle' }
  /** Open the camp menu — fire, food and rest (M3-06). */
  | { readonly kind: 'camp' }
  /** Open the gestures — what the mute have instead of words (M4-07). */
  | { readonly kind: 'gesture' }
  /** Move quietly, or stop (M5-03). Held stance, toggled by a press. */
  | { readonly kind: 'sneak' }
  /** Open the character sheet — who you are becoming (RC2-03). */
  | { readonly kind: 'sheet' }
  /** Enter a melee exchange with an adjacent actor (RC2-05). */
  | { readonly kind: 'engage' }
  /** Break off an active melee exchange (RC2-05). */
  | { readonly kind: 'disengage' }
  /** Loose an arrow at the currently locked ranged target (RC2-06). */
  | { readonly kind: 'loose' };

export type Keymap = Readonly<Record<string, Action>>;

/** Actions the player holds down, as opposed to the ones they press. */
export function isHeld(action: Action): boolean {
  return action.kind === 'move';
}

function move(direction: Direction): Action {
  return { kind: 'move', direction };
}

export const DEFAULT_KEYMAP: Keymap = {
  // Arrows. Diagonals need a second scheme, which is why the others exist.
  ArrowUp: move('n'),
  ArrowDown: move('s'),
  ArrowLeft: move('w'),
  ArrowRight: move('e'),

  // Vi keys, with the diagonal row above them.
  KeyH: move('w'),
  KeyJ: move('s'),
  KeyK: move('n'),
  KeyL: move('e'),
  KeyY: move('nw'),
  KeyU: move('ne'),
  KeyB: move('sw'),
  KeyN: move('se'),

  // Numpad, laid out as it looks.
  Numpad8: move('n'),
  Numpad2: move('s'),
  Numpad4: move('w'),
  Numpad6: move('e'),
  Numpad7: move('nw'),
  Numpad9: move('ne'),
  Numpad1: move('sw'),
  Numpad3: move('se'),

  // M0-06. `x` for examine is the roguelike convention and is worth keeping
  // even though `l` (look) is taken by the vi-key for east. Enter opens the
  // chat line — the same key every chat in every game opens with.
  KeyX: { kind: 'examine' },

  // M2-06. `a` for aim is where a hand already is when it is on the movement
  // keys, and the aim menu's own hotkeys are pressed with the same finger a
  // moment later. `g` guards. The battle view is `v` rather than the ticket's
  // `L`, which the vi-keys have spoken for since M0-05 — and rather than Tab,
  // which would take focus traversal away from the body diagram's buttons.
  KeyA: { kind: 'aim' },
  KeyG: { kind: 'guard' },
  KeyV: { kind: 'battle' },

  // RC2-05. `z` squares up to the adjacent person; `d` breaks off.  Both are
  // deliberately commands, not held movement, so neither can turn into a
  // stream of attack requests.  `d` remains available while the aim menu is
  // open because that menu intercepts its labelled keys first.
  KeyZ: { kind: 'engage' },
  KeyD: { kind: 'disengage' },
  KeyO: { kind: 'loose' },
  KeyT: { kind: 'target' },

  // M2-07. `e` is the interact key every game in the genre uses, and the verb
  // menu it opens works exactly like the aim menu: one key to open, one key to
  // choose. It is deliberately *not* on a movement key — the whole point of
  // these verbs is that you are standing still over somebody.
  KeyE: { kind: 'interact' },

  // M3-03. `f` forages what is under you and `i` opens the satchel — both the
  // conventional letters, and neither is on a movement key. Foraging is
  // deliberately a *press*, not a hold: it starts a channel that being moved
  // cancels, so a key you could lean on would fight the rule that gives the
  // verb its weight.
  KeyF: { kind: 'forage' },
  KeyI: { kind: 'satchel' },

  // M3-05. `r` for "strike a light" because every nearer letter is spoken for —
  // `l` is east on the vi keys, `f` forages and `i` opens the satchel — and
  // because it sits under the same finger as the movement row, which is where a
  // hand is when the light goes.
  KeyR: { kind: 'kindle' },

  // M3-06. Six verbs behind one key, exactly like the aim and verb menus: `c`
  // for camp opens the list and a second keystroke chooses. Six bindings of
  // their own would have collided with the movement keys on somebody's layout,
  // and the two-keystroke pattern is one players already know by here.
  KeyC: { kind: 'camp' },

  // M4-07. `q` opens the gestures — point, beckon, warn. Its own key rather
  // than an entry in the camp menu, because it is *speech* for anybody the
  // cauldron has silenced, and speech does not live two keystrokes deep behind
  // a list of chores. `q` because every nearer letter is taken and because a
  // hand on the movement keys can reach it without looking.
  KeyQ: { kind: 'gesture' },

  // M5-03. `s` for the stalk. It is the last letter on the home row that was
  // not already a movement key or a menu, and it wants to be reachable without
  // looking because the moment you need it is the moment you have just seen a
  // stag and have not yet been seen back.
  KeyS: { kind: 'sneak' },

  // RC2-03. `p` opens the character sheet — the "person" you are becoming. Its
  // own key rather than a menu entry because it is a full-screen surface you stop
  // to read, and `p` is a free letter that is not on a movement key or a menu:
  // `c` is the camp and every nearer letter is spoken for.
  KeyP: { kind: 'sheet' },
  Enter: { kind: 'say' },
  NumpadEnter: { kind: 'say' },
  Escape: { kind: 'cancel' },
};

/** Where a player's overrides are kept. Character-agnostic, for now. */
export const KEYMAP_STORAGE_KEY = 'annwn.keymap';

/**
 * Merge saved overrides over the defaults.
 *
 * Overrides are a *patch*, not a replacement: a player who rebinds one key in
 * 2026 should still get whatever movement keys we add in 2027, and binding to
 * `null` is how they say "unbind this" rather than "I have no bindings".
 */
export function resolveKeymap(overrides: unknown, base: Keymap = DEFAULT_KEYMAP): Keymap {
  if (typeof overrides !== 'object' || overrides === null) return base;

  const resolved: Record<string, Action> = { ...base };

  for (const [code, action] of Object.entries(overrides as Record<string, unknown>)) {
    if (action === null) {
      delete resolved[code];
      continue;
    }
    const parsed = parseAction(action);
    if (parsed) resolved[code] = parsed;
  }

  return resolved;
}

const DIRECTIONS = new Set<string>(['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw']);

function parseAction(value: unknown): Action | null {
  if (typeof value !== 'object' || value === null) return null;
  const action = value as { kind?: unknown; direction?: unknown };

  if (
    action.kind === 'say' ||
    action.kind === 'examine' ||
    action.kind === 'cancel' ||
    action.kind === 'aim' ||
    action.kind === 'target' ||
    action.kind === 'guard' ||
    action.kind === 'sneak' ||
    action.kind === 'battle' ||
    action.kind === 'interact' ||
    action.kind === 'forage' ||
    action.kind === 'satchel' ||
    action.kind === 'kindle' ||
    action.kind === 'camp' ||
    action.kind === 'sheet' ||
    action.kind === 'engage' ||
    action.kind === 'disengage' ||
    action.kind === 'loose'
  ) {
    return { kind: action.kind };
  }

  if (action.kind !== 'move') return null;
  if (typeof action.direction !== 'string' || !DIRECTIONS.has(action.direction)) return null;

  return move(action.direction as Direction);
}

/** Read overrides from storage, tolerating anything that is in there. */
export function loadKeymap(storage: Pick<Storage, 'getItem'> | undefined): Keymap {
  const raw = storage?.getItem(KEYMAP_STORAGE_KEY);
  if (!raw) return DEFAULT_KEYMAP;

  try {
    return resolveKeymap(JSON.parse(raw));
  } catch {
    // A corrupt binding file must never be the reason a player cannot walk.
    return DEFAULT_KEYMAP;
  }
}
